import { Dispatch, SetStateAction, useEffect, useRef, useState, useCallback } from 'react';

export const fetchJson = async (input: RequestInfo, init?: RequestInit) => {
  try {
    const res = await fetch(
      input,
      Object.assign(
        {
          headers: {
            'Content-Type': 'application/json',
          },
        },
        init,
      ),
    );
    if (res.ok) {
      const data = await res.json();
      if (!data.hasOwnProperty('error')) {
        return data;
      } else {
        throw new Error(data.error.message);
      }
    } else {
      let data;
      try {
        data = await res.json();
      } catch (e) {
        //do nothing
      }
      if (data && data.hasOwnProperty('error')) {
        throw new Error(data.error.message);
      } else {
        throw new Error('Request failed with error code: ' + res.status + ' ' + res.statusText);
      }
    }
  } catch (e) {
    throw e;
  }
};

interface useFetchData<T> {
  data?: T;
  error?: Error;
  isLoading: boolean;
}

type LoadFunction<T> = (res?: T) => void | null;
type PromBase<T> = [((value: T | PromiseLike<T>) => void) | undefined, ((reason?: any) => void) | undefined];

function use<T>(
  fetch: Promise<T>,
  setData: Dispatch<SetStateAction<useFetchData<T>>>,
  onLoad?: LoadFunction<T>,
  promRef?: React.MutableRefObject<PromBase<T>>,
): void {
  fetch
    .then((result) => {
      setData({
        data: result,
        error: undefined,
        isLoading: false,
      });
      onLoad && onLoad(result);
      promRef?.current[0]?.(result);
    })
    .catch((error) => {
      console.log(error);
      setData({
        data: undefined,
        error,
        isLoading: false,
      });
      promRef?.current[1]?.(error);
    });
}

export const useFetchJson = <T>(input: RequestInfo, init?: RequestInit): useFetchData<T> => {
  const [data, setData] = useState({
    data: undefined,
    error: undefined,
    isLoading: true,
  } as useFetchData<T>);

  useEffect(() => {
    use<T>(fetchJson(input, init), setData);
    setData({
      data: undefined,
      error: undefined,
      isLoading: true,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [input]);

  return data;
};

interface QueryBase {
  [key: string]: string;
}

type Query = QueryBase | null;

export const useLazyFetchJson = <T>(
  input: RequestInfo,
  init?: RequestInit,
): {
  fetch: (input?: Query, init?: RequestInit) => Promise<T>;
  onLoad: (f: LoadFunction<T>) => void;
} & useFetchData<T> => {
  const [data, setData] = useState({
    data: undefined,
    error: undefined,
    isLoading: false,
  } as useFetchData<T>);
  const [query, setQuery] = useState({} as QueryBase);
  const [req, setReq] = useState({} as RequestInit);
  const [onLoad, setOnLoad] = useState(() => (() => {}) as LoadFunction<T>);
  const promiseRef = useRef<PromBase<T>>([undefined, undefined]);

  useEffect(() => {
    if (data.isLoading) {
      use<T>(
        fetchJson(
          input + (Object.keys(query).length > 0 ? '?' + new URLSearchParams(query).toString() : ''),
          Object.assign(init ?? { method: 'GET' }, req),
        ),
        setData,
        onLoad,
        promiseRef,
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data.isLoading]);

  const onLoadCallback = useCallback(
    (f: LoadFunction<T>) => {
      setOnLoad(() => f);
    },
    [setOnLoad],
  );

  return Object.assign(
    {
      fetch: (input?: Query, init?: RequestInit) => {
        setQuery(input || {});
        setReq(init || {});
        setData({
          data: data.data,
          error: data.error,
          isLoading: true,
        });
        return new Promise<T>((a, r) => {
          promiseRef.current = [a, r];
        });
      },
      onLoad: onLoadCallback,
    },
    data,
  );
};
